import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { votingApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import {
  Vote,
  ArrowLeft,
  Check,
  Clock,
  Users,
  Calendar,
  CheckCircle2,
  BarChart3,
  ChevronRight,
  Loader2,
  AlertCircle,
  FileText,
  Download,
  ExternalLink,
  Shield,
  Timer,
} from 'lucide-react';

export default function Voting() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [votes, setVotes] = useState([]);
  const [selectedVote, setSelectedVote] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [view, setView] = useState('list'); // 'list' | 'vote' | 'results'
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('active');

  const hoaId = user?.hoa_id || 'demo_hoa';

  useEffect(() => {
    fetchVotes();
  }, [filter, hoaId]);

  const fetchVotes = async () => {
    try {
      setLoading(true);
      const status = filter === 'all' ? null : filter;
      const data = await votingApi.list(hoaId, status);
      setVotes(data || []);
    } catch (error) {
      console.error('Error fetching votes:', error);
      toast.error('Failed to load votes');
      setVotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async () => {
    if (!selectedOption) {
      toast.error('Please select an option');
      return;
    }

    setSubmitting(true);
    try {
      await votingApi.cast(selectedVote.vote_id, selectedOption);
      toast.success('Your vote has been submitted securely!');
      
      // Update local state
      const updatedVotes = votes.map(v => {
        if (v.vote_id === selectedVote.vote_id) {
          const newResults = { ...v.results };
          newResults[selectedOption] = (newResults[selectedOption] || 0) + 1;
          return { 
            ...v, 
            results: newResults, 
            user_voted: true, 
            voters_count: (v.voters_count || 0) + 1 
          };
        }
        return v;
      });
      setVotes(updatedVotes);
      setSelectedVote(updatedVotes.find(v => v.vote_id === selectedVote.vote_id));
      setView('results');
    } catch (error) {
      console.error('Error casting vote:', error);
      toast.error(error.message || 'Failed to submit vote');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return '';
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    const days = Math.ceil(diff / 86400000);
    const hours = Math.ceil(diff / 3600000);
    
    if (diff < 0) return 'Ended';
    if (hours <= 3) return `${hours}h left`;
    if (days === 0) return 'Ends today';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  };

  const getTimeUrgency = (endDate) => {
    if (!endDate) return 'normal';
    const end = new Date(endDate);
    const now = new Date();
    const hoursLeft = (end - now) / 3600000;
    
    if (hoursLeft <= 3) return 'critical';
    if (hoursLeft <= 24) return 'urgent';
    return 'normal';
  };

  const getQuorumProgress = (vote) => {
    const total = vote.total_units || 1;
    const voted = vote.voters_count || 0;
    return (voted / total) * 100;
  };

  const getWinningOption = (vote) => {
    if (!vote.results || Object.keys(vote.results).length === 0) return null;
    let maxVotes = 0;
    let winner = null;
    Object.entries(vote.results).forEach(([option, count]) => {
      if (count > maxVotes) {
        maxVotes = count;
        winner = option;
      }
    });
    return winner;
  };

  // Vote Detail View
  if (view === 'vote' && selectedVote) {
    const urgency = getTimeUrgency(selectedVote.end_date);
    
    return (
      <div className="min-h-screen bg-slate-50 pb-24" data-testid="voting-cast-view">
        <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => { setView('list'); setSelectedOption(''); }} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Cast Your Vote</h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6 space-y-6">
          {/* Security Notice */}
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-700">
            <Shield className="w-4 h-4 flex-shrink-0" />
            <span>Your vote is anonymous and secure. Only one vote per unit is allowed.</span>
          </div>

          <Card data-testid="vote-card">
            <CardContent className="p-6">
              {/* Deadline Badge */}
              <Badge className={`mb-3 ${
                urgency === 'critical' ? 'bg-red-100 text-red-700' :
                urgency === 'urgent' ? 'bg-amber-100 text-amber-700' :
                'bg-sky-100 text-sky-700'
              }`}>
                <Timer className="w-3 h-3 mr-1" />
                {getDaysRemaining(selectedVote.end_date)}
              </Badge>
              
              <h2 className="text-xl font-bold font-manrope text-slate-900 mb-2">{selectedVote.title}</h2>
              <p className="text-slate-600 mb-4">{selectedVote.description}</p>
              
              {/* Attachments */}
              {selectedVote.attachments && selectedVote.attachments.length > 0 && (
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-2">Attachments</p>
                  <div className="space-y-2">
                    {selectedVote.attachments.map((att, idx) => (
                      <a 
                        key={idx}
                        href={`${process.env.REACT_APP_BACKEND_URL}${att.file_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700"
                      >
                        <FileText className="w-4 h-4" />
                        {att.file_name}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
                <div className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  <span>{selectedVote.voters_count || 0}/{selectedVote.total_units || 0} voted</span>
                </div>
                <div className="flex items-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  <span>Quorum: {selectedVote.quorum_required}%</span>
                </div>
              </div>

              {/* Participation Progress */}
              <div className="space-y-2 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Participation Progress</span>
                  <span className="font-medium">{Math.round(getQuorumProgress(selectedVote))}%</span>
                </div>
                <Progress value={getQuorumProgress(selectedVote)} className="h-2" />
                {getQuorumProgress(selectedVote) >= selectedVote.quorum_required ? (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Quorum reached
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">
                    {selectedVote.quorum_required - Math.round(getQuorumProgress(selectedVote))}% more needed for quorum
                  </p>
                )}
              </div>

              {/* Voting Options */}
              <div className="space-y-2 mb-4">
                <p className="text-sm font-medium text-slate-700">Select your vote:</p>
              </div>
              
              <RadioGroup value={selectedOption} onValueChange={setSelectedOption} className="space-y-3">
                {(selectedVote.options || []).map((option) => {
                  const candidate = (selectedVote.candidates || []).find(c => c.name === option);
                  return (
                    <label
                      key={option}
                      className={`flex flex-col gap-2 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedOption === option
                          ? 'border-sky-500 bg-sky-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`option-${option.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value={option} />
                        <span className="font-medium text-slate-900">{option}</span>
                        {candidate && (
                          <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Candidate</span>
                        )}
                      </div>
                      {candidate?.bio && (
                        <p className="text-sm text-slate-600 ml-7">{candidate.bio}</p>
                      )}
                    </label>
                  );
                })}
              </RadioGroup>
            </CardContent>
          </Card>

          <Button 
            className="w-full h-12 bg-slate-900 hover:bg-slate-800 rounded-full"
            onClick={handleVote}
            disabled={submitting || !selectedOption}
            data-testid="submit-vote-btn"
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5 mr-2" />
                Submit Vote
              </>
            )}
          </Button>

          <p className="text-xs text-center text-slate-400">
            Once submitted, your vote cannot be changed.
          </p>
        </main>
      </div>
    );
  }

  // Results View
  if (view === 'results' && selectedVote) {
    const totalVotes = Object.values(selectedVote.results || {}).reduce((a, b) => a + b, 0);
    const winner = getWinningOption(selectedVote);
    const quorumMet = getQuorumProgress(selectedVote) >= selectedVote.quorum_required;

    return (
      <div className="min-h-screen bg-slate-50 pb-24" data-testid="voting-results-view">
        <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView('list')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">
              {selectedVote.status === 'closed' ? 'Final Results' : 'Current Results'}
            </h1>
          </div>
        </header>

        <main className="max-w-md mx-auto px-4 py-6 space-y-6">
          <Card data-testid="results-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {selectedVote.status === 'closed' ? (
                  <Badge className="bg-slate-100 text-slate-700">Closed</Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-700">Live Results</Badge>
                )}
                {selectedVote.user_voted && (
                  <Badge className="bg-sky-100 text-sky-700">
                    <Check className="w-3 h-3 mr-1" />
                    You Voted
                  </Badge>
                )}
                {quorumMet ? (
                  <Badge className="bg-emerald-100 text-emerald-700">Quorum Met</Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700">Quorum Not Met</Badge>
                )}
              </div>
              
              <h2 className="text-xl font-bold font-manrope text-slate-900 mb-2">{selectedVote.title}</h2>
              <p className="text-sm text-slate-500 mb-6">{selectedVote.description}</p>

              {/* Results */}
              {totalVotes > 0 ? (
                <div className="space-y-4">
                  {(selectedVote.options || []).map((option) => {
                    const votes = selectedVote.results?.[option] || 0;
                    const percentage = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;
                    const isWinner = option === winner;

                    return (
                      <div key={option} className="space-y-2" data-testid={`result-${option.toLowerCase().replace(/\s/g, '-')}`}>
                        <div className="flex items-center justify-between">
                          <span className={`font-medium ${isWinner ? 'text-emerald-600' : 'text-slate-700'}`}>
                            {option}
                            {isWinner && selectedVote.status === 'closed' && (
                              <CheckCircle2 className="w-4 h-4 inline ml-2" />
                            )}
                          </span>
                          <span className="text-sm text-slate-500">
                            {votes} vote{votes !== 1 ? 's' : ''} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <Progress 
                          value={percentage} 
                          className={`h-3 ${isWinner ? '[&>div]:bg-emerald-500' : ''}`}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <BarChart3 className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No votes cast yet</p>
                </div>
              )}

              {/* Stats Summary */}
              <div className="mt-6 pt-4 border-t border-slate-100 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Total Votes</span>
                  <span className="font-semibold text-slate-900">{totalVotes}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Participation</span>
                  <span className="font-semibold text-slate-900">
                    {selectedVote.voters_count || 0}/{selectedVote.total_units || 0} units ({Math.round(getQuorumProgress(selectedVote))}%)
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Quorum Required</span>
                  <span className="font-semibold text-slate-900">{selectedVote.quorum_required}%</span>
                </div>
                {selectedVote.status === 'active' && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Closes</span>
                    <span className="font-semibold text-amber-600">{formatDate(selectedVote.end_date)}</span>
                  </div>
                )}
              </div>

              {/* Final Result Card */}
              {selectedVote.status === 'closed' && winner && (
                <div className={`mt-4 p-4 rounded-lg ${quorumMet ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
                  <div className="flex items-center gap-2">
                    {quorumMet ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    )}
                    <div>
                      <p className={`font-semibold ${quorumMet ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {quorumMet ? 'Resolution Passed' : 'Quorum Not Reached'}
                      </p>
                      <p className={`text-sm ${quorumMet ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {quorumMet 
                          ? `"${winner}" won with ${((selectedVote.results[winner] || 0) / totalVotes * 100).toFixed(1)}% of votes`
                          : 'Result may not be binding due to insufficient participation'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Button 
            variant="outline"
            className="w-full h-12 rounded-full"
            onClick={() => setView('list')}
          >
            Back to All Votes
          </Button>
        </main>
      </div>
    );
  }

  // List View (default)
  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="voting-list-view">
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" data-testid="back-to-dashboard-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-lg font-semibold font-manrope text-slate-900">Voting</h1>
            <p className="text-xs text-slate-500">Participate in building decisions</p>
          </div>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['active', 'closed', 'all'].map((status) => (
            <Button
              key={status}
              variant={filter === status ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full capitalize whitespace-nowrap ${filter === status ? 'bg-slate-900' : ''}`}
              onClick={() => setFilter(status)}
              data-testid={`filter-${status}`}
            >
              {status}
              {status === 'active' && votes.filter(v => v.status === 'active').length > 0 && (
                <Badge className="ml-1.5 h-5 w-5 p-0 flex items-center justify-center bg-sky-500 text-white">
                  {votes.filter(v => v.status === 'active').length}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Votes List */}
        <div className="space-y-3">
          {loading ? (
            <>
              <Skeleton className="h-36 rounded-xl" />
              <Skeleton className="h-36 rounded-xl" />
              <Skeleton className="h-36 rounded-xl" />
            </>
          ) : votes.length > 0 ? (
            votes.map((vote, index) => {
              const urgency = getTimeUrgency(vote.end_date);
              
              return (
                <Card 
                  key={vote.vote_id}
                  className="overflow-hidden animate-slideUp cursor-pointer hover:shadow-md transition-shadow"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`vote-card-${vote.vote_id}`}
                  onClick={() => {
                    setSelectedVote(vote);
                    if (vote.user_voted || vote.status === 'closed') {
                      setView('results');
                    } else {
                      setView('vote');
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Badges */}
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {vote.status === 'active' ? (
                            <Badge className={`${
                              urgency === 'critical' ? 'bg-red-100 text-red-700' :
                              urgency === 'urgent' ? 'bg-amber-100 text-amber-700' :
                              'bg-emerald-100 text-emerald-700'
                            }`}>
                              {urgency === 'critical' || urgency === 'urgent' ? (
                                <Timer className="w-3 h-3 mr-1" />
                              ) : null}
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-slate-100 text-slate-600">Closed</Badge>
                          )}
                          {vote.user_voted && (
                            <Badge className="bg-sky-100 text-sky-700">
                              <Check className="w-3 h-3 mr-1" />
                              Voted
                            </Badge>
                          )}
                        </div>
                        
                        <h3 className="font-semibold text-slate-900 truncate">{vote.title}</h3>
                        <p className="text-sm text-slate-500 line-clamp-2 mt-1">{vote.description}</p>
                        
                        {/* Stats */}
                        <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {vote.voters_count || 0}/{vote.total_units || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {getDaysRemaining(vote.end_date)}
                          </span>
                          {vote.attachments && vote.attachments.length > 0 && (
                            <span className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              {vote.attachments.length}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <Button 
                        size="sm"
                        className={
                          vote.status === 'active' && !vote.user_voted 
                            ? 'bg-sky-500 hover:bg-sky-600 text-white' 
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedVote(vote);
                          if (vote.user_voted || vote.status === 'closed') {
                            setView('results');
                          } else {
                            setView('vote');
                          }
                        }}
                      >
                        {vote.status === 'active' && !vote.user_voted ? 'Vote' : 'Results'}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Vote className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="font-semibold text-slate-900 mb-1">No votes found</h3>
                <p className="text-slate-500 text-sm">
                  {filter === 'active' 
                    ? 'No active votes at the moment. Check back later!' 
                    : 'No voting history available.'
                  }
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
